import { DateTime } from 'luxon';

import {
  AccessorialCode,
  IShipmentStop,
  ShippingPointDetails,
  ZonedDateTimeRange,
} from '@rygen/rygen-ui/model';
import { loadLuxonDateTime } from '@rygen/rygen-ui/util';

import i18n from '@/i18n';

import { EmbeddableContact } from '../EmbeddableContact';
import { EmbeddableLocation } from '../EmbeddableLocation';
import { ReferenceNumber } from '../ReferenceNumber';
import { ShippingPoint } from '../ShippingPoint';

import {
  SHIPMENT_STOP_DROP_OFF_TYPES,
  SHIPMENT_STOP_PICKUP_TYPES,
  ShipmentStopType,
} from './ShipmentStopType';

const TYPE = 'Shipment';

export class ShipmentStop implements IShipmentStop, ShippingPoint {
  id?: number;
  sequenceNumber?: number;
  stopType?: ShipmentStopType;
  shipOrderShippingPointIds?: number;
  location: EmbeddableLocation = new EmbeddableLocation();
  shippingPointDetails: ShippingPointDetails = new ShippingPointDetails();
  contact: EmbeddableContact = new EmbeddableContact();
  quotedLegMileage?: number;
  estimatedLegMileage?: number;
  requestedAppointmentEarliest?: DateTime;
  requestedAppointmentLatest?: DateTime;
  scheduledAppointmentEarliest?: DateTime;
  requestedAppointmentDateLock: boolean = false;
  requestedAppointmentTimeLock: boolean = false;
  scheduledAppointmentLatest?: DateTime;
  expectedArrival?: DateTime;
  actualArrival?: DateTime;
  actualDeparture?: DateTime;
  requestedAccessorials: AccessorialCode[] = [];
  // Reference numbers entered at the stop level that are promoted to order-level reference numbers
  // during order generation from direct shipment entry.
  orderReferenceNumbers: ReferenceNumber[] = [];
  saveToAddressBook: boolean = false;
  /**
   * A stable identifier used in manual shipment entry when `id` isn't set yet
   */
  _uuid = window.crypto.randomUUID();

  constructor(opts?: Partial<ShipmentStop>) {
    if (opts?.id != null) {
      this.id = opts.id;
    }
    if (opts?.sequenceNumber != null) {
      this.sequenceNumber = opts.sequenceNumber;
    }
    if (opts?.stopType != null) {
      if (typeof opts?.stopType === 'string') {
        this.stopType = ShipmentStopType[opts.stopType];
      } else {
        this.stopType = opts.stopType;
      }
    }
    if (opts?.shipOrderShippingPointIds != null) {
      this.shipOrderShippingPointIds = opts.shipOrderShippingPointIds;
    }
    if (opts?.location != null) {
      this.location = new EmbeddableLocation(opts.location);
    }
    if (opts?.shippingPointDetails != null) {
      this.shippingPointDetails = new ShippingPointDetails(
        opts.shippingPointDetails,
      );
    }
    if (opts?.contact != null) {
      this.contact = new EmbeddableContact(opts.contact);
    }
    this.quotedLegMileage = opts?.quotedLegMileage;
    this.estimatedLegMileage = opts?.estimatedLegMileage;
    this.requestedAppointmentEarliest = loadLuxonDateTime(
      opts?.requestedAppointmentEarliest,
      opts?.location?.timeZone,
    );
    this.requestedAppointmentLatest = loadLuxonDateTime(
      opts?.requestedAppointmentLatest,
      opts?.location?.timeZone,
    );
    this.scheduledAppointmentEarliest = loadLuxonDateTime(
      opts?.scheduledAppointmentEarliest,
      opts?.location?.timeZone,
    );
    this.scheduledAppointmentLatest = loadLuxonDateTime(
      opts?.scheduledAppointmentLatest,
      opts?.location?.timeZone,
    );
    this.expectedArrival = loadLuxonDateTime(
      opts?.expectedArrival,
      opts?.location?.timeZone,
    );
    this.actualArrival = loadLuxonDateTime(
      opts?.actualArrival,
      opts?.location?.timeZone,
    );
    this.actualDeparture = loadLuxonDateTime(
      opts?.actualDeparture,
      opts?.location?.timeZone,
    );
    if (opts?.requestedAccessorials != null) {
      this.requestedAccessorials = opts.requestedAccessorials.map(
        (val) => new AccessorialCode(val),
      );
    }
    if (opts?.orderReferenceNumbers != null) {
      this.orderReferenceNumbers = opts.orderReferenceNumbers.map(
        (val) => new ReferenceNumber(val),
      );
    }
    if (opts?.saveToAddressBook != null) {
      this.saveToAddressBook = opts.saveToAddressBook;
    }
    if (opts?.requestedAppointmentDateLock != null) {
      this.requestedAppointmentDateLock = opts.requestedAppointmentDateLock;
    }
    if (opts?.requestedAppointmentTimeLock != null) {
      this.requestedAppointmentTimeLock = opts.requestedAppointmentTimeLock;
    }
    if (opts?._uuid) {
      this._uuid = opts._uuid;
    }
  }

  static isShipmentStop(location: ShippingPoint): location is ShipmentStop {
    return location.type === TYPE;
  }

  get type() {
    return TYPE;
  }

  get isInProgress() {
    return !!this.actualArrival && !this.actualDeparture;
  }

  get isPickup() {
    return (
      !!this.stopType && SHIPMENT_STOP_PICKUP_TYPES.includes(this.stopType)
    );
  }

  get isDropOff() {
    return (
      !!this.stopType && SHIPMENT_STOP_DROP_OFF_TYPES.includes(this.stopType)
    );
  }

  get key() {
    return JSON.stringify(this);
  }

  get requestedAppointmentRange() {
    return new ZonedDateTimeRange({
      start: this.requestedAppointmentEarliest,
      end: this.requestedAppointmentLatest,
    });
  }

  get stopLabel(): string {
    const stopName = [
      this.location.locationName,
      this.location.city,
      this.location.state,
    ]
      .filter((x) => !!x?.trim())
      .join(', ');

    const stopNumber = i18n.global.t('common.paramNum', {
      param: i18n.global.t('shipments.stops'),
      num: this.sequenceNumber,
    });

    return [stopNumber, stopName].join(' - ');
  }

  get uuid() {
    return this._uuid;
  }

  isResolved(requireBothDate: boolean = false) {
    return !!this.actualDeparture && (!requireBothDate || !!this.actualArrival);
  }
}
